export const formatMoney = (cents, currency = 'usd') =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(
		(cents ?? 0) / 100
	);

export const formatDate = (iso) =>
	iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

const FREQUENCY = {
	weekly: { noun: 'Weekly', per: 'week' },
	bi_weekly: { noun: 'Every 2 weeks', per: '2 weeks' },
	monthly: { noun: 'Monthly', per: 'month' },
};

export const frequencyNoun = (frequency) => FREQUENCY[frequency]?.noun ?? frequency ?? '—';

/** "$1,247.50 / month" */
export const amountPerPeriod = (cents, currency, frequency) =>
	`${formatMoney(cents, currency)}${FREQUENCY[frequency] ? ` / ${FREQUENCY[frequency].per}` : ''}`;

const BRAND_LABELS = {
	visa: 'Visa',
	mastercard: 'Mastercard',
	amex: 'Amex',
	discover: 'Discover',
	diners: 'Diners',
	jcb: 'JCB',
	unionpay: 'UnionPay',
};

/**
 * "Visa •••• 4242" / "Chase •••• 6789". Bank PMs carry bank_name (list
 * endpoint) or mirror it into brand (status endpoint's default chip).
 */
export const paymentMethodLabel = (pm) => {
	if (!pm) return null;
	const name =
		pm.type === 'us_bank_account'
			? (pm.bank_name ?? 'Bank account')
			: (BRAND_LABELS[pm.brand?.toLowerCase?.()] ?? pm.brand ?? 'Card');

	return `${name} •••• ${pm.last4 ?? '????'}`;
};

/**
 * "12/31" for a card, null for a bank account (which has no expiry). The
 * expiry is what tells two "Visa •••• 4242" rows apart, so every surface that
 * names a method shows it next to the label.
 */
export const paymentMethodExpiry = (pm) =>
	pm?.exp_month ? `${String(pm.exp_month).padStart(2, '0')}/${String(pm.exp_year).slice(-2)}` : null;

/** The per-cycle amount the next invoice will actually charge. */
export const assignmentChargeCents = (assignment) => assignment?.next_charge_amount_cents ?? assignment?.amount_cents;

/**
 * The API prices the service fee on the payer's current default rail and,
 * when the school passes fees, on each rail it accepts
 * (`processing_fee_by_rail`, `total_by_rail`, keyed 'card' /
 * 'us_bank_account'). While the parent is choosing a method the quote
 * follows the choice, so a bank account is quoted at the bank rate rather
 * than the card fee the default happened to carry.
 */
const breakdownForRail = (breakdown, rail) => {
	if (!breakdown || !rail) return breakdown;
	const fee = breakdown.processing_fee_by_rail?.[rail];
	const total = breakdown.total_by_rail?.[rail];
	if (fee === undefined || total === undefined) return breakdown;

	return { ...breakdown, processing_fee_cents: fee, total_cents: total };
};

/**
 * Amount rows for the plan summary box. With fees/discounts on the next
 * cycle: Base amount, then indented Fees / Discounts / Total. Without
 * modifiers the breakdown is noise, so it collapses to a single Amount row.
 */
export const amountBreakdownRows = (assignment, rail = null) => {
	const { currency, frequency } = assignment ?? {};
	const breakdown = breakdownForRail(assignment?.breakdown, rail);

	// Accrued on overdue invoices; lands on the next invoice ON TOP of the
	// recurring total, so it renders as its own row after Total.
	const lateFeeRow = breakdown?.late_fees_cents
		? [
				{
					label: 'Late fees (next invoice)',
					value: `+ ${formatMoney(breakdown.late_fees_cents, currency)}`,
					indent: true,
				},
			]
		: [];

	if (!breakdown || (!breakdown.fees_cents && !breakdown.discounts_cents && !breakdown.processing_fee_cents)) {
		return [
			{ label: 'Amount', value: amountPerPeriod(assignmentChargeCents(assignment), currency, frequency) },
			...lateFeeRow,
		];
	}

	return [
		{ label: 'Base amount', value: amountPerPeriod(breakdown.base_cents, currency, frequency) },
		...(breakdown.fees_cents
			? [{ label: 'Fees', value: `+ ${formatMoney(breakdown.fees_cents, currency)}`, indent: true }]
			: []),
		...(breakdown.discounts_cents
			? [{ label: 'Discounts', value: `− ${formatMoney(breakdown.discounts_cents, currency)}`, indent: true }]
			: []),
		// Pass-through service fee (schools that pass fees on) — part of
		// Total, priced on the rail the parent is about to pick.
		...(breakdown.processing_fee_cents
			? [{ label: 'Service fee', value: `+ ${formatMoney(breakdown.processing_fee_cents, currency)}`, indent: true }]
			: []),
		{ label: 'Total', value: amountPerPeriod(breakdown.total_cents, currency, frequency), indent: true },
		...lateFeeRow,
	];
};

/**
 * The "Applied to" line on the post-Checkout receipt: the care window the
 * invoice covers and the child, "Sep 7 – Sep 13, 2026 · Maya". A one-off
 * fee or manual invoice has no window, so it names the invoice instead.
 */
export const appliedToLabel = (appliedTo) => {
	if (!appliedTo) return null;
	const what = appliedTo.period_label ?? (appliedTo.invoice_number ? `Invoice ${appliedTo.invoice_number}` : 'Invoice');

	return appliedTo.student_first_name ? `${what} · ${appliedTo.student_first_name}` : what;
};
